<script lang="ts">
  /**
   * Invite links — pre-bound registration links a superadmin sends to a community
   * owner, and their status.
   *
   * The creator-accounts table that used to sit under them moved to
   * `/admin/accounts` (AdminAccounts.svelte), which shows every account with its
   * plan, communities, affiliate and sign-in history rather than the four columns
   * that fitted here. Two tables of the same accounts was one too many; this page is
   * now about the links alone.
   */
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    parseTs,
    timeAgo,
    type AdminUser,
  } from '../lib/admin';
  import { ensureCommunities } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { createInvite, fetchAllInvites, type Invite } from '@api';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let invites = $state<Invite[]>([]);

  // Create-invite form
  let skoolUrl = $state('');
  let note = $state('');
  let creating = $state(false);
  let createError = $state<string | null>(null);
  let lastInvite = $state<Invite | null>(null);

  let copied = $state<string | null>(null); // token whose link was just copied
  let copyTimer: ReturnType<typeof setTimeout> | undefined;

  const linkFor = (token: string) => `${location.origin}/register?invite=${token}`;

  async function copyLink(token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(linkFor(token));
    } catch {
      return;
    }
    copied = token;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => (copied = null), 1600);
  }

  /**
   * ONE round trip (doc 04 §4.1). The invite list goes out with the bootstrap rather
   * than behind `fetchMe()` → `ensureCommunities()`, and it is not superadmin-gated on
   * the client — the server is. A creator who lands here spends one swallowed 403 on
   * the way to the redirect, which is the price of not waiting a round trip to find
   * out who is asking.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    let pageErr: unknown = null;
    const pending = fetchAllInvites().catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      if (me.role !== 'superadmin') {
        navigate('/admin');
        return;
      }
      await ensureCommunities();
      const loaded = await pending;
      if (loaded) invites = loaded.items;
      else if (pageErr) throw pageErr;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  async function submitCreate(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (creating || !skoolUrl.trim()) return;
    creating = true;
    createError = null;
    try {
      const trimmedNote = note.trim();
      const row = await createInvite({
        skoolUrl: skoolUrl.trim(),
        ...(trimmedNote ? { note: trimmedNote } : {}),
      });
      lastInvite = row;
      skoolUrl = '';
      note = '';
      await copyLink(row.token); // copy the fresh link right away
      // `POST /v1/invites` returns the invite it created, newest-first is the list's
      // own order, so the row goes straight on the front — no re-read (W9).
      invites = [row, ...invites];
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      createError = err instanceof Error ? err.message : 'Could not create the invite.';
    } finally {
      creating = false;
    }
  }

  // Pretty community label; falls back to a prettified slug when the API
  // doesn't know the name yet (community not ingested).
  function communityLabel(row: Invite): string {
    if (row.community_name) return row.community_name;
    const slug = (row.skool_url ?? '').replace(/\/+$/, '').split('/').pop() ?? '';
    return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || row.skool_url;
  }

  const CHIP_BASE =
    'rounded px-1.5 py-px font-mono text-[0.6rem] uppercase tracking-wide';
  const STATUS_CHIP: Record<string, string> = {
    pending: 'bg-primary/10 text-primary',
    used: 'bg-success/10 text-success',
    expired: 'bg-base-content/10 text-base-content/68',
  };

  // `parseTs`, never `new Date` — a tz-less instant is read as LOCAL time, which
  // shifts a date by the viewer's offset (lib/admin.ts:147).
  const fmtDate = (iso: string | null | undefined) =>
    iso
      ? parseTs(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : '—';
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <PageHeader section="Super admin" title="Invites" />
      <!-- Create an invite -->
      <section class="rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Send an invite</div>
        <h2 class="mt-1 font-display text-xl font-semibold">Invite a community owner</h2>
        <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
          Pre-binds a signup link to their Skool community. Send them the link — registering
          through it skips the URL step and starts their trial.
        </p>

        <form class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" onsubmit={submitCreate}>
          <label class="block flex-1">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Skool community URL</span>
            <input
              type="url"
              class="input input-bordered input-sm w-full bg-base-100 font-mono text-xs"
              bind:value={skoolUrl}
              placeholder="https://www.skool.com/their-community"
              required
            />
          </label>
          <label class="block flex-1">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Note</span>
            <input
              type="text"
              class="input input-bordered input-sm w-full bg-base-100 text-xs"
              bind:value={note}
              placeholder="Recipient name / label (optional)"
            />
          </label>
          <button
            type="submit"
            class="btn btn-primary btn-sm gap-2"
            disabled={creating || !skoolUrl.trim()}
          >
            {#if creating}<span class="loading loading-spinner loading-xs"></span>{/if}
            Create invite link
          </button>
        </form>

        {#if createError}
          <div class="mt-3 rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
            {createError}
          </div>
        {/if}

        {#if lastInvite}
          <div class="mt-4 rounded-field border hair bg-base-100/60 p-3">
            <div class="eyebrow mb-1.5 text-[0.6rem]">
              Invite for {communityLabel(lastInvite)} — send this link
            </div>
            <div class="flex items-center gap-2">
              <input
                type="text"
                readonly
                class="input input-bordered input-sm flex-1 bg-base-100 font-mono text-xs"
                value={linkFor(lastInvite.token)}
                onfocus={(e) => (e.target as HTMLInputElement).select()}
              />
              <button class="btn btn-sm" onclick={() => copyLink(lastInvite!.token)}>
                {copied === lastInvite.token ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          </div>
        {/if}
      </section>

      <!-- Invite links -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Invite links</div>
        <div class="mt-3 overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr class="text-[0.65rem] uppercase tracking-wide text-base-content/65">
                <th>Community</th>
                <th>Note</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#if invites.length === 0}
                <tr><td colspan="6" class="py-4 text-sm text-base-content/68">No invites yet.</td></tr>
              {:else}
                {#each invites as row (row.id)}
                  <tr>
                    <td class="text-sm font-medium">{communityLabel(row)}</td>
                    <td class="redact text-sm text-base-content/70">{row.note ?? '—'}</td>
                    <td class="whitespace-nowrap text-xs text-base-content/72">{timeAgo(row.created_at)}</td>
                    <td class="whitespace-nowrap text-xs text-base-content/72">{fmtDate(row.expires_at)}</td>
                    <td>
                      <span class="{CHIP_BASE} {STATUS_CHIP[row.status] ?? STATUS_CHIP['expired']}">
                        {row.status}
                      </span>
                      {#if row.status === 'used' && row.used_by_email}
                        <span class="redact ml-1 font-mono text-[0.65rem] text-base-content/68">{row.used_by_email}</span>
                      {/if}
                    </td>
                    <td class="text-right">
                      {#if row.status === 'pending'}
                        <button class="btn btn-ghost btn-xs" onclick={() => copyLink(row.token)}>
                          {copied === row.token ? 'Copied!' : 'Copy link'}
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </div>
      </section>

      <p class="mt-3 text-xs text-base-content/55">
        Plans, communities and affiliate details for every account live on
        <button class="link link-hover text-primary" onclick={() => navigate('/admin/accounts')}>Accounts</button>.
      </p>
    </PageContainer>
  {/if}
</AdminShell>
